import {useEffect, useMemo, useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import axios from "axios"
import {Alert, Card, Col, Row, Spinner} from "react-bootstrap"
import TenantAccessManager from "./TenantAccessManager"
import TenantTree from "../exam-management/components/TenantTree"
import PersonTenantSearch from "../exam-management/components/PersonTenantSearch"
import {TenantTreeNode} from "../exam-management/components/tenant.types"
import {useAuth} from "../../context/AuthProvider"
import {useSearchParams} from "react-router-dom"

const filterTreeToManagedTenants = (
    nodes: TenantTreeNode[],
    managedIds: Set<string>
): TenantTreeNode[] => nodes.flatMap(node => {
    const children = filterTreeToManagedTenants(node.children, managedIds)

    if (!managedIds.has(node._id) && children.length === 0) {
        return []
    }

    return [{...node, children}]
})

const flattenTree = (nodes: TenantTreeNode[]): TenantTreeNode[] =>
    nodes.flatMap(node => [node, ...flattenTree(node.children)])

export default function TenantOverview() {
    const auth = useAuth()
    const {t} = useTranslation(["common", "tenant", "user"])
    const [searchParams, setSearchParams] = useSearchParams()
    const requestedTenantId = useRef(searchParams.get("tenant"))
    const [managedTenants, setManagedTenants] = useState<TenantTreeNode[]>([])
    const [tree, setTree] = useState<TenantTreeNode[]>([])
    const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null)
    const [personHighlightedIds, setPersonHighlightedIds] = useState<string[]>([])
    const [focusRequestKey, setFocusRequestKey] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const managementRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        setLoading(true)
        Promise.all([
            axios.get<TenantTreeNode[]>("/api/tenant/my", auth.axiosAuthHeader()),
            axios.get<TenantTreeNode[]>("/api/tenant/tree", auth.axiosAuthHeader()),
        ])
            .then(([managedResponse, treeResponse]) => {
                const managed = Array.isArray(managedResponse.data) ? managedResponse.data : []
                setManagedTenants(managed)
                setTree(Array.isArray(treeResponse.data) ? treeResponse.data : [])
                setSelectedTenantId(current => current
                    ?? managed.find(tenant => tenant._id === requestedTenantId.current)?._id
                    ?? managed[0]?._id
                    ?? null)
                setError(null)
            })
            .catch(() => setError(t("overview.loadError", {ns: "tenant"})))
            .finally(() => setLoading(false))
    }, [auth, t])

    const managedIds = useMemo(
        () => new Set(managedTenants.map(tenant => tenant._id)),
        [managedTenants]
    )
    const visibleTree = useMemo(
        () => filterTreeToManagedTenants(tree, managedIds),
        [tree, managedIds]
    )
    const searchableTenants = useMemo(
        () => flattenTree(visibleTree).filter(tenant => managedIds.has(tenant._id)),
        [visibleTree, managedIds]
    )
    const jumpToTenant = (tenant: TenantTreeNode) => {
        setSelectedTenantId(tenant._id)
        setFocusRequestKey(previous => previous + 1)
    }

    const openManagement = (tenantId: string) => {
        setSelectedTenantId(tenantId)
        setSearchParams({tenant: tenantId}, {replace: true})
        window.requestAnimationFrame(() => {
            managementRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            })
        })
    }

    const refreshTree = () => {
        axios.get<TenantTreeNode[]>("/api/tenant/tree", auth.axiosAuthHeader())
            .then(response => {
                setTree(Array.isArray(response.data) ? response.data : [])
            })
            .catch(() => setError(t("overview.loadError", {ns: "tenant"})))
    }

    const highlightedIds = personHighlightedIds

    if (loading) {
        return <Card><Card.Body className="d-flex gap-2 align-items-center">
            <Spinner animation="border" size="sm"/>
            {t("overview.loading", {ns: "tenant"})}
        </Card.Body></Card>
    }

    return <div className="component">
        {error && <Alert variant="danger">{error}</Alert>}
        <Row className="g-3">
            <Col xs={12} lg={5} xl={4}>
                <Card>
                    <Card.Header><strong>{t("overview.title", {ns: "tenant"})}</strong></Card.Header>
                    <Card.Body>
                        <PersonTenantSearch
                            tenants={searchableTenants}
                            onHighlight={setPersonHighlightedIds}
                            onTenantSelect={tenantId => {
                                const tenant = searchableTenants.find(candidate =>
                                    candidate._id === tenantId
                                )

                                if (tenant) jumpToTenant(tenant)
                            }}
                            onTenantManage={tenantId => {
                                if (managedIds.has(tenantId)) {
                                    openManagement(tenantId)
                                }
                            }}
                        />

                        <TenantTree
                            nodes={visibleTree}
                            selectedTenantId={selectedTenantId}
                            highlightedTenantIds={highlightedIds}
                            highlightedLabel={t("overview.searchResult", {ns: "tenant"})}
                            revealTenantIds={highlightedIds}
                            focusTenantId={selectedTenantId}
                            focusRequestKey={focusRequestKey}
                            onSelect={tenant => {
                                if (managedIds.has(tenant._id)) {
                                    openManagement(tenant._id)
                                }
                            }}
                        />

                        {managedTenants.length === 0 && (
                            <div className="text-muted">{t("overview.empty", {ns: "tenant"})}</div>
                        )}
                    </Card.Body>
                </Card>
            </Col>

            <Col xs={12} lg={7} xl={8}>
                <div ref={managementRef}>
                    {selectedTenantId ? (
                        <TenantAccessManager
                            tenantId={selectedTenantId}
                            onTenantChanged={refreshTree}
                        />
                    ) : (
                        <Card><Card.Body className="text-muted">
                            {t("access.noTenantSelected", {ns: "tenant"})}
                        </Card.Body></Card>
                    )}
                </div>
            </Col>
        </Row>
    </div>
}
